import React from "react";

const FALLBACKS = {
  default: `${process.env.CDN_ENDPOINT || ""}/player-placeholder-dark.svg`,
  jockey: `${process.env.CDN_ENDPOINT || ""}/Horse+Racing/Jockey+Images/Rider_no_image.png`,
};

// Some upstream image hosts (e.g. matchbook silks) block cross-origin requests and
// return 403/NotSameOrigin for every image. Once we've seen an origin fail, there's no
// point re-requesting any image from it — go straight to the fallback. Remembered for
// the session so live re-renders and navigation don't re-trigger the blocked requests.
const FAILED_IMAGE_ORIGINS_KEY = "horsesImageFailedOrigins";

const getHttpOrigin = (url) => {
  if (typeof url !== "string" || !/^https?:\/\//i.test(url)) return null;
  try {
    return new URL(url).origin;
  } catch {
    return null;
  }
};

const loadFailedOrigins = () => {
  try {
    const raw = sessionStorage.getItem(FAILED_IMAGE_ORIGINS_KEY);
    return new Set(raw ? JSON.parse(raw) : []);
  } catch {
    return new Set();
  }
};

const failedImageOrigins = loadFailedOrigins();

const isOriginBlocked = (url) => {
  const origin = getHttpOrigin(url);
  return origin ? failedImageOrigins.has(origin) : false;
};

const markOriginBlocked = (url) => {
  const origin = getHttpOrigin(url);
  if (!origin || failedImageOrigins.has(origin)) return;
  failedImageOrigins.add(origin);
  try {
    sessionStorage.setItem(FAILED_IMAGE_ORIGINS_KEY, JSON.stringify([...failedImageOrigins]));
  } catch {
    // sessionStorage unavailable (private mode/quota) — in-memory Set still applies.
  }
};

/**
 * Jockey/participant image - base64 or URL. variant "jockey" for horses/football placeholder.
 */
export const HorsesJockeyImage = ({
  runnerImage = "",
  runnerName = "",
  height = "40px",
  width = "40px",
  whiteBackground = false,
  fallbackImageUrl,
  variant = "default",
}) => {
  const resolvedFallback = fallbackImageUrl || FALLBACKS[variant] || FALLBACKS.default;

  const handleError = (e) => {
    const img = e.target;
    markOriginBlocked(img.src);
    if (img.src !== resolvedFallback) {
      img.src = resolvedFallback;
    }
  };

  function handleLoad(event) {
    const img = event.target;
    if (img.naturalHeight > img.naturalWidth) {
      img.style.maxHeight = height;
    }
  }

  const requestedSrc = runnerImage && runnerImage.trim() ? runnerImage : resolvedFallback;
  // Skip the request entirely for origins we've already seen return forbidden/blocked.
  const src = isOriginBlocked(requestedSrc) ? resolvedFallback : requestedSrc;

  return (
    <div
      className={`flex items-center justify-center flex-shrink-0 overflow-hidden ${whiteBackground ? "bg-white" : ""}`}
      style={{ width, height, minWidth: width, minHeight: height }}
    >
      <img
        src={src}
        onError={handleError}
        alt={runnerName || "Participant"}
        onLoad={handleLoad}
        style={{
          width,
          height,
          minWidth: width,
          minHeight: height,
          objectFit: "cover",
          display: "block",
          verticalAlign: "middle",
        }}
        loading="lazy"
        decoding="async"
      />
    </div>
  );
};

export default HorsesJockeyImage;
